import { useState } from "react";

interface NumberFieldProps {
  label: string;
  value: number | undefined;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  description?: string;
  hint?: string;
  inactiveDefault?: number;
}

export function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  description,
  hint,
  inactiveDefault,
}: NumberFieldProps) {
  const displayValue = value ?? 0;
  const isDefault = inactiveDefault !== undefined && (value === undefined || value === inactiveDefault);
  const [showTip, setShowTip] = useState(false);

  return (
    <div className="group/field rounded-radius-sm px-1.5 py-1 transition-colors hover:bg-surface-sunken/50">
      <div className="flex items-center gap-2">
        <div
          className="relative flex w-28 shrink-0 items-center gap-1"
          onMouseEnter={() => { if (description) setShowTip(true); }}
          onMouseLeave={() => setShowTip(false)}
        >
          <span className={`truncate text-[11px] ${isDefault ? "text-text-muted" : "text-text-secondary"}`}>
            {label}
          </span>
          {isDefault && (
            <span className="rounded-radius-full bg-border-subtle px-1 py-px text-[8px] font-medium text-text-muted">off</span>
          )}
          {description && (
            <span className="text-[9px] text-text-muted/40 opacity-0 transition-opacity group-hover/field:opacity-100">?</span>
          )}
          {showTip && description && (
            <div className="absolute left-0 bottom-full z-50 mb-1 w-52 rounded-radius-sm border border-border-subtle bg-surface-overlay px-2.5 py-1.5 text-[10px] leading-relaxed text-text-secondary shadow-md">
              {description}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            value={displayValue}
            onChange={(e) => onChange(Number(e.target.value))}
            min={min}
            max={max}
            step={step}
            className={`h-7 w-16 rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-[11px] focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20 ${
              isDefault ? "text-text-muted" : "text-text-primary"
            }`}
          />
          {min !== undefined && max !== undefined && (
            <input
              type="range"
              value={displayValue}
              onChange={(e) => onChange(Number(e.target.value))}
              min={min}
              max={max}
              step={step}
              className="h-1 w-16 cursor-pointer accent-accent"
            />
          )}
        </div>
      </div>
      {hint && <div className="mt-0.5 pl-28 text-[9px] text-text-muted">{hint}</div>}
    </div>
  );
}
